// NotasCreditoPanel.jsx — Panel de Notas de Crédito (estilo ReporteCob)
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown, ChevronRight, Search,
  Users, Tag, Calendar, RefreshCw, Filter, X, FilterX, Download,
  FileX,  AlertTriangle, Package, DollarSign, 
} from 'lucide-react';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList,
} from 'recharts';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/FacturacionMenuHeader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import FullScreenLoader from './FullScreenLoader';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  TIPO_NC: 'Tipo NC',
  ANULADA: 'Estado',
  BI_PROVEEDOR: 'Proveedor',
  BI_MARCA: 'Marca',
  BI_LINEA: 'Línea',
  BI_SEGMENTO: 'Segmento BI',
  BI_CLASIF2: 'Clasificación',
  BI_ASP: 'ASP',
  CLI_PRIN: 'Cliente Principal',
  CLI_VEN_DES: 'Vendedor',
  CLI_REG_VERD: 'Región',
  CLI_CANAL: 'Canal',
  CLI_CANAL_REAL: 'Canal Real',
  CLI_SEG_DES: 'Segmento',
  CLI_TERRITORIO: 'Territorio',
  CLI_ID_TIPO: 'Tipo Cliente',
  ESPECIALISTA: 'Especialista',
  USER_ESP: 'Especialista User',
};

const INITIAL_FILTERS = {
  TIPO_NC: [],
  ANULADA: ['NO'],
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], BI_SEGMENTO: [], BI_CLASIF2: [], BI_ASP: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_REG_VERD: [], CLI_CANAL: [], CLI_CANAL_REAL: [],
  CLI_SEG_DES: [], CLI_TERRITORIO: [], CLI_ID_TIPO: [],
  ESPECIALISTA: [], USER_ESP: [],
};

const nombresMeses = [
  'ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO',
  'JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE',
];

const MES_A_NUM = {
  ENERO: 1, FEBRERO: 2, MARZO: 3, ABRIL: 4, MAYO: 5, JUNIO: 6,
  JULIO: 7, AGOSTO: 8, SEPTIEMBRE: 9, OCTUBRE: 10, NOVIEMBRE: 11, DICIEMBRE: 12,
};

const MES_ABREV = {
  ENERO: 'ENE', FEBRERO: 'FEB', MARZO: 'MAR', ABRIL: 'ABR',
  MAYO: 'MAY', JUNIO: 'JUN', JULIO: 'JUL', AGOSTO: 'AGO',
  SEPTIEMBRE: 'SEP', OCTUBRE: 'OCT', NOVIEMBRE: 'NOV', DICIEMBRE: 'DIC',
};

// ─── Formateo ─────────────────────────────────────────────────
const parseNumericES = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value).trim();
  if (!s) return 0;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && !hasDot) return Number(s.replace(/\./g, '').replace(',', '.'));
  return Number(s.replace(/,/g, ''));
};

const formatNumber = (num, decimals = 0) => {
  const n = parseNumericES(num);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('es-VE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatUSD = (num) => `$${formatNumber(Math.abs(num), 2)}`;
const formatK = (num) => {
  const n = Math.abs(Number(num || 0));
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return Math.round(n);
};

const COLORS = ['#ef4444', '#3b82f6', '#f59e0b', '#00ba52', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x} y={y} fill="#cbd5e1" fontSize="10" fontWeight="600"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const WRAP_STYLE = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  lineHeight: 1.25,
};


// ══════════════════════════════════════════════════════════════
// MultiSelectFilter
// ══════════════════════════════════════════════════════════════
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange, onClearPill }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? (options.find(opt => (opt.value || opt) === selectedValues[0])?.label ?? selectedValues[0])
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options
    .filter(opt => String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 200);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      onChange(name, filteredOptions.map(opt => opt.value || opt));
    }
  };

  const handleSingleToggle = (optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text" placeholder="Buscar..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onClearPill?.(name)}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const NotasCreditoPanel = () => {
  const {
    ncQueryPanel,
    ncQueryDevTabla,
    ncQueryVentaVsDev,
    getNotasCreditoFilters,
    loading: apiLoading,
  } = useQuery();
  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const filtersInitialized = useRef(false);

  // ─── Panel data (KPIs + trend + pie + bar region) ───────
  const [panelData, setPanelData] = useState(null);

  // ─── Tabla devoluciones mes a mes ────────────────────────
  const [devBreakdownType, setDevBreakdownType] = useState('REG_VEN_CLI_PROD');
  const [devTabla, setDevTabla] = useState(null);
  const [devQuerying, setDevQuerying] = useState(false);
  const [expandedDevNodes, setExpandedDevNodes] = useState({});

  // ─── Venta vs Devoluciones ───────────────────────────────
  const [ventaDevData, setVentaDevData] = useState(null);
  const [ventaDevQuerying, setVentaDevQuerying] = useState(false);

  // ─── Filtros ───
  const currentYear = new Date().getFullYear();
  const currentMonthName = nombresMeses[new Date().getMonth()];

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [selectedMeses, setSelectedMeses] = useState([currentMonthName]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewType, setViewType] = useState('mensual');

  const requestRef = useRef(0);

  // ══════════════════════════════════════════════════════════════
  // CONFIG INICIAL
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadConfig = async () => {
      if (filtersInitialized.current) return;
      setLocalLoading(true);
      try {
        const config = await getNotasCreditoFilters();
        if (!config) throw new Error('Configuración de NC no disponible');

        const fullOptions = {};
        Object.keys(INITIAL_FILTERS).forEach(key => {
          const raw = config.availableOptions?.[key];
          if (Array.isArray(raw)) {
            fullOptions[key] = raw.map(item =>
              typeof item === 'string' ? item : (item?.value || item?.label || '')
            ).filter(Boolean);
          } else {
            fullOptions[key] = [];
          }
        });

        fullOptions.TIPO_NC = ['DEVOLUCION', 'FINANCIERA'];
        fullOptions.ANULADA = ['NO', 'SI'];

        setAvailableOptions(fullOptions);
        setAvailableYears(config.availableYears || [currentYear]);

        if (config.currentYear) setSelectedAnios([config.currentYear]);
        else if (config.availableYears?.[0]) setSelectedAnios([config.availableYears[0]]);

        filtersInitialized.current = true;
        setFiltersReady(true);
      } catch (err) {
        console.error('Error cargando config NC:', err);
        filtersInitialized.current = true;
        setFiltersReady(true);
      } finally {
        setLocalLoading(false);
      }
    };
    loadConfig();
  }, []);

  // ══════════════════════════════════════════════════════════════
  // PAYLOAD + FETCH PANEL
  // ══════════════════════════════════════════════════════════════
  const buildBasePayload = useCallback(() => ({
    page_anios: selectedAnios.map(String).filter(Boolean),
    page_meses: selectedMeses.filter(Boolean),
    tipo_nc: null,
    filters,
    user_info: profile
      ? { username: profile.username, cargo: profile.cargo }
      : null,
  }), [filters, selectedAnios, selectedMeses, profile]);

  const fetchPanel = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRef.current;
    try {
      const res = await ncQueryPanel(buildBasePayload());
      if (reqId !== requestRef.current) return;
      setPanelData(res || null);
    } catch (e) {
      console.error('❌ Error panel NC:', e);
      setPanelData(null);
    }
  }, [buildBasePayload, ncQueryPanel, filtersReady]);

  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(async () => {
      setQuerying(true);
      try {
        await fetchPanel();
      } finally {
        setQuerying(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [filters, selectedAnios, selectedMeses, viewType, filtersReady, fetchPanel]);

  // ══════════════════════════════════════════════════════════════
  // FETCH TABLA DEVOLUCIONES (mes a mes, jerárquica)
  // ══════════════════════════════════════════════════════════════
  const fetchDevTabla = useCallback(async () => {
    if (!filtersReady) return;
    setDevQuerying(true);
    try {
      const res = await ncQueryDevTabla({
        page_anios: selectedAnios.map(String).filter(Boolean),
        page_meses: [],
        breakdown_type: devBreakdownType,
        tipo_nc: 'DEVOLUCION',
        filters,
        user_info: profile
          ? { username: profile.username, cargo: profile.cargo }
          : null,
      });
      setDevTabla(res || null);
    } catch (e) {
      console.error('❌ Error dev tabla:', e);
      setDevTabla(null);
    } finally {
      setDevQuerying(false);
    }
  }, [selectedAnios, devBreakdownType, filters, profile, filtersReady, ncQueryDevTabla]);

  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(fetchDevTabla, 350);
    return () => clearTimeout(t);
  }, [selectedAnios, devBreakdownType, filters, filtersReady, fetchDevTabla]);

  // Limpiar expansiones al cambiar de vista
  useEffect(() => {
    setExpandedDevNodes({});
  }, [devBreakdownType, selectedAnios]);

  // ══════════════════════════════════════════════════════════════
  // 🆕 FETCH VENTA vs DEVOLUCIONES (cruza sales_data + NC)
  // ══════════════════════════════════════════════════════════════
  const fetchVentaVsDev = useCallback(async () => {
    if (!filtersReady) return;
    setVentaDevQuerying(true);
    try {
      const res = await ncQueryVentaVsDev({
        page_anios: selectedAnios.map(String).filter(Boolean),
        page_meses: [],           // 🔑 ignora filtro de mes → todos los meses
        tipo_nc: null,
        filters,
        user_info: profile
          ? { username: profile.username, cargo: profile.cargo }
          : null,
      });
      setVentaDevData(res || null);
    } catch (e) {
      console.error('❌ Error venta vs devoluciones:', e);
      setVentaDevData(null);
    } finally {
      setVentaDevQuerying(false);
    }
  }, [selectedAnios, filters, profile, filtersReady, ncQueryVentaVsDev]);

  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(fetchVentaVsDev, 350);
    return () => clearTimeout(t);
  }, [selectedAnios, filters, filtersReady, fetchVentaVsDev]);

  // ══════════════════════════════════════════════════════════════
  // HANDLERS
  // ══════════════════════════════════════════════════════════════
  const handleRefresh = useCallback(async () => {
    setQuerying(true);
    setDevQuerying(true);
    setVentaDevQuerying(true);
    try {
      await Promise.all([fetchPanel(), fetchDevTabla(), fetchVentaVsDev()]);
    } finally {
      setQuerying(false);
      setDevQuerying(false);
      setVentaDevQuerying(false);
    }
  }, [fetchPanel, fetchDevTabla, fetchVentaVsDev]);

  const handleResetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
  }, []);

  const handleClearSingleFilter = useCallback((name) => {
    setFilters(prev => ({ ...prev, [name]: [] }));
  }, []);

  const toggleDevNode = useCallback((path) => {
    setExpandedDevNodes(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);

  // ══════════════════════════════════════════════════════════════
  // DATA DERIVADA — PANEL
  // ══════════════════════════════════════════════════════════════
  const kpis = panelData?.kpis || {};

  // ─── Trend (IGNORA filtro de Mes) ───────────────────────────
  const chartData = useMemo(() => {
    const trend = panelData?.trend_full || panelData?.trend || [];
    if (!trend.length) return [];

    if (viewType === 'anual') {
      const byYear = {};
      trend.forEach(r => {
        const k = String(r.anio);
        if (!byYear[k]) byYear[k] = { name: k, Dev: 0, Fin: 0, Total: 0 };
        byYear[k].Dev   += Math.abs(Number(r.dev_usd || 0));
        byYear[k].Fin   += Math.abs(Number(r.fin_usd || 0));
        byYear[k].Total += Math.abs(Number(r.total_usd || 0));
      });
      return Object.values(byYear)
        .filter(r => r.Total > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    return trend
      .map(r => ({
        name: `${String(r.mes || '').substring(0, 3)}-${r.anio}`,
        anio: String(r.anio),
        mes: r.mes,
        mesNum: MES_A_NUM[String(r.mes || '').toUpperCase()] || 99,
        Dev:   Math.abs(Number(r.dev_usd || 0)),
        Fin:   Math.abs(Number(r.fin_usd || 0)),
        Total: Math.abs(Number(r.total_usd || 0)),
      }))
      .filter(r => r.Total > 0)
      .sort((a, b) => {
        if (a.anio !== b.anio) return a.anio.localeCompare(b.anio);
        return a.mesNum - b.mesNum;
      });
  }, [panelData, viewType]);

  // ─── Pie: Dev vs Fin ────────────────────────────────────────
  const pieTipoData = useMemo(() => {
    return (panelData?.pie_tipo || [])
      .map(r => ({
        name: r.tipo === 'DEVOLUCION' ? 'Devoluciones' : 'Financieras',
        tipo: r.tipo,
        value: Math.abs(Number(r.usd || 0)),
        count: Number(r.cantidad || 0),
      }))
      .filter(x => x.value > 0);
  }, [panelData]);

  // ─── Bar: Regiones (respeta filtro de Mes) ─────────────────
  const barRegionData = useMemo(() => {
    return (panelData?.por_region || [])
      .map(r => ({
        name: r.region || 'SIN REGIÓN',
        value: Math.abs(Number(r.usd || 0)),
        nc_count: Number(r.nc_count || 0),
        clientes: Number(r.clientes || 0),
      }))
      .filter(x => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [panelData]);

  // ══════════════════════════════════════════════════════════════
  // 🆕 DATA DERIVADA — VENTA vs DEVOLUCIONES
  // ══════════════════════════════════════════════════════════════
  const ventaDevChartData = useMemo(() => {
    const rows = ventaDevData?.rows || [];
    return rows
      .map(r => ({
        name: `${String(r.mes || '').substring(0, 3)}-${r.anio}`,
        anio: String(r.anio),
        mes: r.mes,
        mesNum: MES_A_NUM[String(r.mes || '').toUpperCase()] || 99,
        Venta: Math.abs(Number(r.venta_bultos || 0)),
        Devolucion: Math.abs(Number(r.dev_bultos || 0)),
        Proporcion: Number(r.proporcion_pct || 0),
      }))
      .filter(r => r.Venta > 0 || r.Devolucion > 0)
      .sort((a, b) => {
        if (a.anio !== b.anio) return a.anio.localeCompare(b.anio);
        return a.mesNum - b.mesNum;
      });
  }, [ventaDevData]);

  const ventaDevTotales = ventaDevData?.totales || {
    venta_bultos: 0, dev_bultos: 0, proporcion_pct: 0,
  };

  // ══════════════════════════════════════════════════════════════
  // ÁRBOL DEVOLUCIONES (mes a mes)
  // ══════════════════════════════════════════════════════════════
  const devMeses = devTabla?.meses || [];

  const devTree = useMemo(() => {
    const data = devTabla;
    if (!data?.rows?.length || !data?.num_levels) return [];

    const niveles = data.num_levels;
    const meses = data.meses || [];
    const tree = {};

    for (const row of data.rows) {
      const keys = [];
      for (let i = 1; i <= niveles; i++) {
        keys.push(String(row[`c${i}`] || `NIVEL_${i}_VACIO`));
      }

      let bucket = tree;
      for (let i = 0; i < niveles; i++) {
        const k = keys[i];
        const isLeaf = i === niveles - 1;
        const desc = isLeaf ? String(row[`c${niveles}_desc`] || '') : '';

        if (!bucket[k]) {
          bucket[k] = {
            name: k,
            desc,
            level: i + 1,
            isLeaf,
            cantidades: Object.fromEntries(meses.map(m => [m, 0])),
            total_cantidad: 0,
            total_usd: 0,
            nc_count: 0,
            children: {},
          };
        }
        const node = bucket[k];

        for (const m of meses) {
          node.cantidades[m] += Number(row[`m_${m}`] || 0);
        }
        node.total_cantidad += Number(row.total_cantidad || 0);
        node.total_usd      += Number(row.total_usd || 0);
        node.nc_count       += Number(row.nc_count || 0);

        if (!isLeaf) bucket = node.children;
      }
    }

    const toArray = (obj) =>
      Object.values(obj)
        .map(n => ({ ...n, children: toArray(n.children) }))
        .sort((a, b) => b.total_cantidad - a.total_cantidad);

    return toArray(tree);
  }, [devTabla]);

  // Grid dinámico: nombre + N meses + total
  const devGridTemplate = useMemo(() => {
    const cols = ['2.5fr'];
    for (let i = 0; i < devMeses.length; i++) cols.push('0.55fr');
    cols.push('0.7fr');
    return cols.join(' ');
  }, [devMeses]);

  // ══════════════════════════════════════════════════════════════
  // RENDER NODO RECURSIVO
  // ══════════════════════════════════════════════════════════════
  const renderDevNodo = (nodo, path) => {
    const isExpanded = !!expandedDevNodes[path];
    const hasChildren = nodo.children?.length > 0;
    const paddingLeft = (nodo.level - 1) * 16 + 10;

    return (
      <React.Fragment key={path}>
        <div
          className="table-row5"
          style={{
            gridTemplateColumns: devGridTemplate,
            cursor: hasChildren ? 'pointer' : 'default',
            padding: '6px 10px',
            fontSize: '0.72rem',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
          }}
          onClick={() => hasChildren && toggleDevNode(path)}
        >
          <div
            className="col-name"
            style={{
              paddingLeft,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              ...WRAP_STYLE,
            }}
          >
            {hasChildren ? (
              isExpanded
                ? <ChevronDown size={13} style={{ flexShrink: 0 }} />
                : <ChevronRight size={13} style={{ flexShrink: 0 }} />
            ) : (
              <span style={{ width: 13, flexShrink: 0, display: 'inline-block' }} />
            )}
            <span title={nodo.name}>
              {nodo.name}
              {nodo.desc && (
                <span style={{ fontSize: '0.68rem', opacity: 0.65, marginLeft: 6 }}>
                  · {nodo.desc}
                </span>
              )}
            </span>
          </div>

          {devMeses.map(m => (
            <div key={m} className="col-data" style={{ textAlign: 'right' }}>
              {formatNumber(nodo.cantidades[m] || 0)}
            </div>
          ))}

          <div className="col-data bold" style={{ textAlign: 'right' }}>
            {formatNumber(nodo.total_cantidad)}
          </div>
        </div>

        {isExpanded && nodo.children.map(child => renderDevNodo(child, `${path}|${child.name}`))}
      </React.Fragment>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // EXPORT TABLA DEVOLUCIONES
  // ══════════════════════════════════════════════════════════════
  const exportDevTabla = useCallback(() => {
    if (!devTabla?.rows?.length) return;
    const meses = devTabla.meses || [];
    const niveles = devTabla.num_levels || 0;

    const rows = devTabla.rows.map(r => {
      const obj = {};
      for (let i = 1; i <= niveles; i++) {
        obj[`N${i}`] = r[`c${i}`] || '';
      }
      obj['SKU_DESC'] = r[`c${niveles}_desc`] || '';
      meses.forEach(m => {
        obj[MES_ABREV[m] || m] = Number(r[`m_${m}`] || 0);
      });
      obj['TOTAL'] = Number(r.total_cantidad || 0);
      obj['USD'] = Math.round(Number(r.total_usd || 0));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Devoluciones');
    XLSX.writeFile(wb, `NC_Devoluciones_${devBreakdownType}_${Date.now()}.xlsx`);
  }, [devTabla, devBreakdownType]);

  // ══════════════════════════════════════════════════════════════
  // KPI CARDS
  // ══════════════════════════════════════════════════════════════
  const totalUSD    = Math.abs(Number(kpis.total_usd || 0));
  const devUSD      = Math.abs(Number(kpis.dev_usd || 0));
  const finUSD      = Math.abs(Number(kpis.fin_usd || 0));
  const totalDocs   = Number(kpis.total_docs || 0);
  const totalClient = Number(kpis.total_clientes || 0);
  const countDev    = Number(kpis.count_dev || 0);
  const countFin    = Number(kpis.count_fin || 0);
  const anuladas    = Number(kpis.anuladas || 0);
  const totalNc     = Number(kpis.total_nc || 0);

  const kpiCards = [
    {
      key: 'total_docs',
      icon: <FileX size={22} color="white" />,
      label: 'Total Notas',
      value: formatNumber(totalDocs),
      sublabel: `${formatNumber(countDev)} dev · ${formatNumber(countFin)} fin`,
      color: '#3b82f6',
    },
    {
      key: 'total_usd',
      icon: <DollarSign size={22} color="white" />,
      label: 'Monto Total NC',
      value: formatUSD(totalUSD),
      sublabel: `Dev ${formatUSD(devUSD)} · Fin ${formatUSD(finUSD)}`,
      color: '#ef4444',
    },
    {
      key: 'total_clientes',
      icon: <Users size={22} color="white" />,
      label: 'Clientes Afectados',
      value: formatNumber(totalClient),
      sublabel: totalDocs > 0
        ? `${(totalClient / totalDocs).toFixed(2)} clientes/nota`
        : 'Sin registros',
      color: '#8b5cf6',
    },
    {
      key: 'anuladas',
      icon: <AlertTriangle size={22} color="white" />,
      label: 'Anuladas',
      value: formatNumber(anuladas),
      sublabel: totalNc > 0
        ? `${((anuladas / totalNc) * 100).toFixed(1)}% del total`
        : 'Sin registros',
      color: anuladas > 0 ? '#f59e0b' : '#00ba52',
    },
  ];

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: 'easeInOut' }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersReady && (
        <FullScreenLoader message="Cargando datos de Notas de Crédito..." />
      )}

      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: 'easeOut' }}
      >
        {/* ═══ HEADER ═══ */}
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Panel de Notas de Crédito</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} disabled={querying || devQuerying || ventaDevQuerying}>
              <RefreshCw size={16} className={(querying || devQuerying || ventaDevQuerying) ? 'spin-anim' : ''} />
            </button>

            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>

            <div className="switch-toggle-group">
              <button
                className={`switch-toggle-btn ${viewType === 'mensual' ? 'active' : ''}`}
                onClick={() => setViewType('mensual')}
              >
                Mensual
              </button>
              <button
                className={`switch-toggle-btn ${viewType === 'anual' ? 'active' : ''}`}
                onClick={() => setViewType('anual')}
              >
                Anual
              </button>
            </div>

            <div className="date-filters-group">
              <MultiSelectFilter
                label="Mes" name="meses" icon={Calendar}
                options={nombresMeses.map(m => ({ value: m, label: m }))}
                selectedValues={selectedMeses}
                onChange={(_, vals) => setSelectedMeses(vals)}
              />
              <MultiSelectFilter
                label="Año" name="anios" icon={Calendar}
                options={availableYears.map(y => ({ value: String(y), label: String(y) }))}
                selectedValues={selectedAnios.map(String)}
                onChange={(_, vals) =>
                  setSelectedAnios(vals.map(v => Number(v)).filter(n => Number.isFinite(n)))
                }
              />
            </div>
          </div>
        </header>

        {/* ═══ WORKING DAYS BAR ═══ */}
        <div className="working-days-bar">
          <div className="wd-item">
            <span>Total Notas:</span> <strong>{formatNumber(totalDocs)}</strong>
          </div>
          <div className="wd-item">
            <span>Devoluciones:</span> <strong>{formatNumber(countDev)}</strong>
          </div>
          <div className="wd-item">
            <span>Financieras:</span> <strong>{formatNumber(countFin)}</strong>
          </div>
          <div className="wd-separator"></div>
          <div className="wd-item">
            <span>Anuladas:</span> <strong>{formatNumber(anuladas)}</strong>
          </div>
          <div className="wd-separator"></div>
          <div className="wd-item">
            <span>Período:</span>{' '}
            <strong>
              {selectedMeses.length ? selectedMeses.join(', ') : 'Todos'} {selectedAnios.join(', ')}
            </strong>
          </div>
          {filters.TIPO_NC?.length > 0 && (
            <>
              <div className="wd-separator"></div>
              <div className="wd-item">
                <span>Tipo:</span>{' '}
                <strong>{filters.TIPO_NC.map(t => t === 'DEVOLUCION' ? 'Devolución' : 'Financiera').join(', ')}</strong>
              </div>
            </>
          )}
        </div>

        {/* ═══ SIDEBAR FILTROS ═══ */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros de Notas de Crédito</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters}>
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map(key => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={key.includes('CLI') || key === 'ESPECIALISTA' ? Users : Tag}
                options={
                  key === 'TIPO_NC'
                    ? [
                        { value: 'DEVOLUCION', label: 'Devolución' },
                        { value: 'FINANCIERA', label: 'Financiera' },
                      ]
                    : key === 'ANULADA'
                    ? [
                        { value: 'NO', label: 'Vigentes' },
                        { value: 'SI', label: 'Anuladas' },
                      ]
                    : (availableOptions[key] || []).map(o => ({ value: o, label: o }))
                }
                selectedValues={filters[key] || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                onClearPill={handleClearSingleFilter}
              />
            ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* ═══ 4 KPI CARDS ═══ */}
        <div
          className="dashboard-indicators"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: 12,
          }}
        >
          {kpiCards.map(card => (
            <div key={card.key} className="indicator-card v-real">
              <div className="card-content">
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 44, height: 44, borderRadius: '50%',
                    background: card.color, marginBottom: 8,
                  }}
                >
                  {card.icon}
                </div>
                <span className="indicator-label">{card.label}</span>
                <div className="indicator-row">
                  <span className="indicator-value">{card.value}</span>
                </div>
                {card.sublabel && (
                  <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{card.sublabel}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ═══ FILA 1 · TREND + PIE DEV/FIN ═══ */}
        <div className="charts-grid main-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
          <div className="chart-container">
            <h3 className="chart-title">
              Evolución {viewType === 'anual' ? 'Anual' : 'Mensual'} de Notas de Crédito
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#fff' }} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={formatK} tick={{ fontSize: 9, fill: '#fff' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', borderRadius: 8, border: 'none', color: '#333' }}
                  formatter={(value, name) => [formatUSD(value), name]}
                />
                <Legend
                  verticalAlign="bottom" height={16} iconType="circle"
                  wrapperStyle={{
                    fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px', padding: '5px', width: '90%',
                    margin: '0 auto', left: '5%', bottom: 0,
                    display: 'flex', justifyContent: 'center', color: '#fff',
                  }}
                />
                <Bar dataKey="Dev" fill="#f59e0b" radius={[2, 2, 0, 0]} barSize={12} />
                <Bar dataKey="Fin" fill="#3b82f6" radius={[2, 2, 0, 0]} barSize={12}>
                  <LabelList dataKey="Fin" position="top" formatter={formatK} style={{ fontSize: '9px', fill: '#fff' }} />
                </Bar>
                <Line type="monotone" dataKey="Total" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="Total" position="top" formatter={formatK} style={{ fontSize: '9px', fill: '#ef4444', fontWeight: 'bold' }} />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h3 className="chart-title">Distribución Devoluciones · Financieras</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieTipoData}
                  cx="50%" cy="45%"
                  innerRadius={40} outerRadius={65}
                  paddingAngle={4} dataKey="value"
                  labelLine={false} label={renderCustomizedLabel}
                >
                  {pieTipoData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.tipo === 'DEVOLUCION' ? '#f59e0b' : '#3b82f6'}
                      stroke="#fff"
                      strokeWidth={0}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatUSD(value)} />
                <Legend
                  verticalAlign="bottom" height={30} iconType="circle"
                  wrapperStyle={{
                    fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px', padding: '5px', width: '90%',
                    margin: '0 auto', left: '5%', bottom: 0,
                    display: 'flex', justifyContent: 'center', color: '#fff',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ═══ FILA 2 · BAR REGIÓN ═══ */}
        <div className="chart-container mini" style={{ marginTop: '15px' }}>
          <h3 className="chart-title">Monto NC por Región (Top 10)</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barRegionData} layout="vertical" margin={{ top: 5, right: 50, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="#ffffff10" />
              <XAxis type="number" hide />
              <YAxis
                dataKey="name" type="category" axisLine={false} tickLine={false}
                tick={{ fontSize: 10, fill: '#fff' }} width={100}
              />
              <Tooltip cursor={{ fill: 'transparent' }} formatter={(value) => formatUSD(value)} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                {barRegionData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
                <LabelList
                  dataKey="value" position="right"
                  formatter={(v) => formatK(v)}
                  style={{ fill: '#cbd5e1', fontSize: 10, fontWeight: 'bold' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ═══ FILA 2.5 · VENTA vs DEVOLUCIONES (barras + línea proporción) ═══ */}
        <div
          className="chart-container mini"
          style={{
            marginTop: '15px',
            opacity: ventaDevQuerying ? 0.8 : 1,
            transition: 'opacity 0.3s',
          }}
        >
          <h3 className="chart-title">
            Venta vs Devoluciones (Bultos) · Proporción de Devoluciones sobre Venta
            <span style={{ marginLeft: 8, fontSize: '0.65rem', opacity: 0.55, fontWeight: 400 }}>
              (todos los meses del año · ignora filtro de Mes)
            </span>
          </h3>

          {/* Mini-resumen inline */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ color: '#00ba52' }}>
              <strong>Venta Total:</strong> {formatNumber(ventaDevTotales.venta_bultos)} bls
            </span>
            <span style={{ color: '#ef4444' }}>
              <strong>Devoluciones:</strong> {formatNumber(ventaDevTotales.dev_bultos)} bls
            </span>
            <span style={{ color: '#f59e0b' }}>
              <strong>Proporción Global:</strong> {Number(ventaDevTotales.proporcion_pct || 0).toFixed(2)}%
            </span>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={ventaDevChartData}
              margin={{ top: 20, right: 40, left: -10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 9, fill: '#fff' }}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tickFormatter={formatK}
                tick={{ fontSize: 9, fill: '#fff' }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 9, fill: '#f59e0b' }}
                tickFormatter={(v) => `${v}%`}
                domain={[0, (max) => Math.max(5, Math.ceil(max * 1.25))]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  borderRadius: 8,
                  border: 'none',
                  color: '#333',
                }}
                formatter={(value, name) => {
                  if (name === 'Proporción %') return [`${Number(value).toFixed(2)}%`, name];
                  return [`${formatNumber(value)} bls`, name];
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={22}
                iconType="circle"
                wrapperStyle={{
                  fontSize: '10px',
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  borderRadius: '12px',
                  padding: '5px',
                  width: '90%',
                  margin: '0 auto',
                  left: '5%',
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  color: '#333',
                }}
              />

              {/* Barras: Venta (verde) y Devoluciones (rojo) */}
              <Bar
                yAxisId="left"
                dataKey="Venta"
                name="Venta Bultos"
                fill="#3b82f6"
                radius={[2, 2, 0, 0]}
                barSize={16}
              />
              <Bar
                yAxisId="left"
                dataKey="Devolucion"
                name="Devoluciones Bultos"
                fill="#ef4444"
                radius={[2, 2, 0, 0]}
                barSize={16}
              />

              {/* Línea: Proporción % */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="Proporcion"
                name="Proporción %"
                stroke="#00ba52"
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#00ba52' }}
                activeDot={{ r: 5 }}
              >
                <LabelList
                    dataKey="Proporcion"
                    position="top"
                    offset={10}
                    formatter={(v) => (v > 0 ? `${Number(v).toFixed(1)}%` : '')}
                    style={{
                        fontSize: '10px',
                        fill: '#ffffff',
                        fontWeight: 'bold',
                        paintOrder: 'stroke',
                        stroke: '#0f172a',
                        strokeWidth: 3,
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                    }}
                    />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* ═══ FILA 3 · TABLA DEVOLUCIONES MES A MES (JERÁRQUICA) ═══ */}
        <div
          className="chart-container mini"
          style={{
            marginTop: 15,
            padding: 15,
            opacity: devQuerying ? 0.8 : 1,
            transition: 'opacity 0.3s',
          }}
        >
          <div className="table-header-container" style={{ flexWrap: 'wrap', gap: 10 }}>
            <h3 className="chart-title" style={{ margin: 0 }}>
              <Package size={14} style={{ display: 'inline', marginRight: 6 }} />
              NC Devoluciones · Cantidades mes a mes
              <span style={{ marginLeft: 8, fontSize: '0.65rem', opacity: 0.55, fontWeight: 400 }}>
                ({formatNumber(devTabla?.totales?.total_cantidad || 0)} unidades ·
                {' '}{formatNumber(devTabla?.totales?.total_nc || 0)} notas ·
                {' '}todos los meses)
              </span>
            </h3>

            <div className="chart-header-filters" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="switch-toggle-group">
                <button
                  className={`switch-toggle-btn ${devBreakdownType === 'REG_VEN_CLI_PROD' ? 'active' : ''}`}
                  onClick={() => setDevBreakdownType('REG_VEN_CLI_PROD')}
                >
                  Reg / Ven / Cli / Prod
                </button>
                <button
                  className={`switch-toggle-btn ${devBreakdownType === 'CLI_PROD' ? 'active' : ''}`}
                  onClick={() => setDevBreakdownType('CLI_PROD')}
                >
                  Cliente / Prod
                </button>
                <button
                  className={`switch-toggle-btn ${devBreakdownType === 'PROD' ? 'active' : ''}`}
                  onClick={() => setDevBreakdownType('PROD')}
                >
                  Producto
                </button>
              </div>

              <button
                className="export-table-btn"
                onClick={exportDevTabla}
                disabled={!devTabla?.rows?.length}
                title="Exportar a Excel"
              >
                <Download size={12} />
              </button>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            {devTree.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', opacity: 0.5 }}>
                {devQuerying ? 'Cargando devoluciones...' : 'Sin devoluciones para los filtros actuales.'}
              </div>
            ) : (
              <div
                style={{
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {/* HEADER */}
                <div
                  className="table-header5"
                  style={{
                    gridTemplateColumns: devGridTemplate,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    fontSize: '0.7rem',
                    padding: '8px 10px',
                    opacity: 0.85,
                  }}
                >
                  <div>Jerarquía</div>
                  {devMeses.map(m => (
                    <div key={m} style={{ textAlign: 'right' }} title={m}>
                      {MES_ABREV[m] || m}
                    </div>
                  ))}
                  <div style={{ textAlign: 'right' }}>Total</div>
                </div>

                {/* BODY */}
                {devTree.map(nodo => renderDevNodo(nodo, nodo.name))}

                {/* FOOTER TOTALES */}
                <div
                  className="table-footer5"
                  style={{
                    gridTemplateColumns: devGridTemplate,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    padding: '8px 10px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                  }}
                >
                  <div>TOTALES GENERALES</div>
                  {devMeses.map(m => (
                    <div key={m} style={{ textAlign: 'right' }}>
                      {formatNumber(devTabla?.totales?.cantidades?.[m] || 0)}
                    </div>
                  ))}
                  <div style={{ textAlign: 'right' }}>
                    {formatNumber(devTabla?.totales?.total_cantidad || 0)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="status-footer">
          {querying
            ? '🔄 SINCRONIZANDO NOTAS DE CRÉDITO...'
            : `📋 PANEL NOTAS DE CRÉDITO · ${selectedMeses.join(', ')} ${selectedAnios.join(', ')} · ${formatNumber(totalDocs)} notas · ${formatUSD(totalUSD)}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default NotasCreditoPanel;